import React from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { DownloadLink, TextRow } from 'components';
import { Images, previewFile, renderSize } from 'utils';
import { WISHLIST_ATTACHMENT_TYPES } from '../constants';

/**
 * Attachment list for a wishlist request, in the shared uploaded-file markup
 * (`loaded-documents-box` et al., see components/table/fileComponents/uploadFiles.js).
 *
 * `files` are either saved `StoredFile`s or staged local files
 * `{ localId, file }` that upload on submit. Saved files download through the
 * shared <DownloadLink>, which mints a fresh signed URL from the key. Read-only
 * when `onRemove` is omitted.
 */
export const WishlistAttachmentList = ({ files = [], onRemove, disabled }) => {
    if (!files.length) return null;

    return (
        <div className="uploaded-files-wrapper">
            {files.map((f, i) => {
                const name = f.file?.name || f.originalName;
                const bytes = f.file?.size ?? f.sizeBytes;
                const size = bytes || bytes === 0 ? renderSize({ size: bytes }) : '';
                const type = (f.file?.type || f.contentType || f.format || '').replace('image/', '').replace('application/', '');
                return (
                    <div
                        className="loaded-documents-box"
                        key={f.localId || f.key || i}
                        style={{ margin: '0 0 8px 0', flexDirection: 'column' }}
                    >
                        <div className="uploaded-file-info-header">
                            <div className="space-between">
                                <div className="flex-align-center" style={{ gap: 8, width: '85%' }}>
                                    <img src={Images.uploadedFile} alt="icon" />
                                    <p className="loaded-document-name full-width">
                                        <TextRow name={name} fontWeight={600} textWidth={10} />
                                    </p>
                                </div>
                                <div className="flex-align-center" style={{ gap: 8 }}>
                                    {f?.key && (
                                        <button
                                            type="button"
                                            className="reset-btn"
                                            style={{ padding: 0, height: 'auto' }}
                                            onClick={() => previewFile(f)}
                                        >
                                            <img style={{ height: 20 }} src={Images.showEye} alt="view" />
                                        </button>
                                    )}
                                    {f.key && <DownloadLink file={f} small />}
                                    {onRemove && (
                                        <button
                                            type="button"
                                            className="reset-btn-by-default"
                                            disabled={disabled}
                                            onClick={() => onRemove(f)}
                                        >
                                            <img src={Images.redTrashLine} alt="remove" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="uploaded-file-detail-box">
                                {size && <p>{size}</p>}
                                {size && type && <div className="loaded-circle" />}
                                {type && <p>{type}</p>}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/** Drag-and-drop zone in the shared markup. Files are staged locally; nothing uploads until submit. */
export const WishlistAttachmentDropzone = ({ onAdd, disabled }) => {
    const add = (fileOrList) => {
        const files = fileOrList instanceof FileList ? Array.from(fileOrList) : Array.isArray(fileOrList) ? fileOrList : [fileOrList];
        onAdd(files.filter(Boolean));
    };

    return (
        <div style={{ border: 'none' }} className="upload-file-header-box">
            <FileUploader handleChange={add} name="file" multiple types={WISHLIST_ATTACHMENT_TYPES} maxSize={10} disabled={disabled}>
                <div className="drag-drop-component">
                    <div className="upload-dock-icon">
                        <img src={Images.uploadDockCloud} alt="upload" style={{ margin: 0 }} />
                    </div>
                    <label className="custom-file-upload">
                        <input
                            onClick={(event) => (event.target.value = null)}
                            onChange={(event) => add(event.target.files)}
                            type="file"
                            multiple
                            accept=".png,.jpg,.jpeg,.webp,.pdf,.csv"
                        />
                        <i className="fa fa-cloud-upload" />
                        <div className="upload">
                            <p>
                                <span style={{ fontWeight: 600 }}>Drag & drop</span>
                                <span style={{ margin: '0 4px' }}>your file here or</span>
                                <span style={{ color: '#2E6DD8', fontWeight: 600 }}>click to upload</span>
                            </p>
                            <p style={{ fontSize: '12px', color: '#667085' }}>PDF, PNG, JPG, WEBP, CSV &middot; up to 10 MB each</p>
                        </div>
                    </label>
                </div>
            </FileUploader>
        </div>
    );
};
